import { useEffect, useState, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import Button from "../components/Button";
import InputField from "../components/InputField";
import api from "../api/api";
import Breadcrumbs from "../components/Breadcrumbs";
import Pagination from "../components/Pagination";
import Tabs from "../components/Tabs";
import * as bootstrap from "bootstrap";
import Badge from "../components/Badge";
import "../components/Modal.css";

const WarehouseBookings = () => {
  const [activeTab, setActiveTab] = useState("pending");

  const [loading, setLoading] = useState(false);
 
  const [pendingData, setPendingData] = useState([]);
  const [completedData, setCompletedData] = useState([]);
 
  const [currentPagePending, setCurrentPagePending] = useState(1);
  const [currentPageCompleted, setCurrentPageCompleted] = useState(1);
 
  const [itemsPerPage, setItemsPerPage] = useState(5);
 
  const [selectedItem, setSelectedItem] = useState(null);
  const [verifiedProductName, setVerifiedProductName] = useState("");
  const [modalErrors, setModalErrors] = useState({});
  const [currentDateTime, setCurrentDateTime] = useState("");
 
  const tabsConfig = [
    { label: "Pending", value: "pending" },
    { label: "Completed", value: "completed" },
  ];
 
  const fetchPending = async () => {
    setLoading(true);
    try {
      const response = await api("/warehouse/pending-bookings");
      setPendingData(response.data.bookings || []);
    } catch (err) {
      console.error("Error fetching pending bookings:", err);
    } finally {
      setLoading(false);
    }
  };
 
  const fetchCompleted = async () => {
    setLoading(true);
    try {
      const response = await api("/warehouse/completed-bookings");
      setCompletedData(response.data.bookings || []);
    } catch (err) {
      console.error("Error fetching completed bookings:", err);
    } finally {
      setLoading(false);
    }
  };
 
  useEffect(() => {
    fetchPending();
    fetchCompleted();
  }, []);
 
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
      .format(date)
      .replace(/,/g, "");
  };
 
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";

    const formatted = new Date(dateString).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return formatted.replace(", ", " "); // Remove unwanted comma
  };
  const getCurrentDateTime = useCallback(() => formatDateTime(new Date()), []);

  useEffect(() => {
    setCurrentDateTime(getCurrentDateTime());
    const interval = setInterval(() => {
      setCurrentDateTime(getCurrentDateTime());
    }, 60000);

    return () => clearInterval(interval);
  }, [getCurrentDateTime]);
 
  const pendingPages = Math.ceil(pendingData.length / itemsPerPage);
  const completedPages = Math.ceil(completedData.length / itemsPerPage);
 
  const currentPending = pendingData.slice(
    (currentPagePending - 1) * itemsPerPage,
    currentPagePending * itemsPerPage
  );
 
  const currentCompleted = completedData.slice(
    (currentPageCompleted - 1) * itemsPerPage,
    currentPageCompleted * itemsPerPage
  );
 
  const handleEnterClick = (item) => {
    setSelectedItem(item);
    setVerifiedProductName("");
    const modal = new bootstrap.Modal(document.getElementById("enterModal"));
    modal.show();
  };
 
  const handleExitClick = (item) => {
    setSelectedItem(item);
    const modal = new bootstrap.Modal(document.getElementById("exitModal"));
    modal.show();
  };
 
  const handleOkayEnter = async () => {
    setModalErrors({});
 
    if (!verifiedProductName.trim()) {
      setModalErrors({
        verifiedProductName: "Verified Product Name is required",
      });
      return;
    }
 
    if (
      verifiedProductName.trim().toLowerCase() !==
      selectedItem.product_name.trim().toLowerCase()
    ) {
      setModalErrors({ verifiedProductName: "Product does not match!" });
      return;
    }
 
    try {
      await api.post("/warehouse/enter-product", {
        id: selectedItem.id,
        verified_product_name: verifiedProductName,
        entry_time: currentDateTime,
      });
 
      await fetchPending();
      const modal = bootstrap.Modal.getInstance(
        document.getElementById("enterModal")
      );
      modal.hide();
    } catch (err) {
      alert("Failed to enter product");
    }
  };
 
  const handleOkayExit = async () => {
    try {
      await api.post("/warehouse/exit-product", { id: selectedItem.id });
 
      await fetchPending();
      await fetchCompleted();
 
      const modal = bootstrap.Modal.getInstance(
        document.getElementById("exitModal")
      );
      modal.hide();
    } catch {
      alert("Error exiting product");
    }
  };
 
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
 
  return (
    <>
      <Breadcrumbs />
 
      <Tabs
        tabs={tabsConfig}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
 
      {activeTab === "pending" && (
        <>
          {loading && <CircularProgress />}
 
          <div className="position-relative">
            <div
              className={`table-responsive table-fixed-header overflow-auto ${
                itemsPerPage >= 20 ? "mb-80" : itemsPerPage > 11 ? "mb-3" : ""
              }`}
            >
              <table
                className={`table table-bordered table-hover ${
                  itemsPerPage >= 20 ? "mb-3" : itemsPerPage < 11 ? "mb-0" : ""
                }`}
              >
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th>Storage Type</th>
                    <th>Warehouse</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
 
                <tbody>
                  {currentPending.length > 0 ? (
                    currentPending.map((item) => (
                      <tr key={item.id}>
                        <td>{item.customer_name}</td>
                        <td>{item.storage_type}</td>
                        <td>{item.warehouse_name}</td>
                        <td>{formatDate(item.start_date)}</td>
                        <td>{formatDate(item.end_date)}</td>
                        <td>
                          <Badge text={item.status} className="gray" />
                        </td>
                        <td>
                          {item.status !== "Arrived" ? (
                            <Button
                              variant="success"
                              onClick={() => handleEnterClick(item)}
                              btncls="mt-0 me-2 fw-normal custombtn"
                            >
                              Enter
                            </Button>
                          ) : (
                            <Button
                              variant="danger"
                              onClick={() => handleExitClick(item)}
                              btncls="mt-0 custombtn"
                            >
                              Exit
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No Data Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
 
          <Pagination
            currentPage={currentPagePending}
            totalPages={pendingPages}
            onPageChange={setCurrentPagePending}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </>
      )}
 
      {activeTab === "completed" && (
        <>
          {loading && <CircularProgress />}
 
          <div className="position-relative">
            <div
              className={`table-responsive table-fixed-header overflow-auto ${
                itemsPerPage >= 20 ? "mb-80" : itemsPerPage > 11 ? "mb-3" : ""
              }`}
            >
              <table
                className={`table table-bordered table-hover ${
                  itemsPerPage >= 20 ? "mb-3" : itemsPerPage < 11 ? "mb-0" : ""
                }`}
              >
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th>Warehouse</th>
                    <th>Entry Time</th>
                    <th>Exit Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
 
                <tbody>
                  {currentCompleted.length > 0 ? (
                    currentCompleted.map((item) => (
                      <tr key={item.id}>
                        <td>{item.customer_name}</td>
                        <td>{item.warehouse_name}</td>
                        <td>{formatDateTime(item.entry_time)}</td>
                        <td>{formatDateTime(item.exit_time)}</td>
 
                        <td>
                          {" "}
                          <Badge text={item.status} className="gray" />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No Data Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
 
          <Pagination
            currentPage={currentPageCompleted}
            totalPages={completedPages}
            onPageChange={setCurrentPageCompleted}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </>
      )}
      <div
        className="modal fade"
        id="enterModal"
        tabIndex="-1"
        aria-labelledby="enterModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="enterModalLabel">
                Mark Enter
              </h5>
              <Button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></Button>
            </div>
            <div className="modal-body">
              <p className="mb-3">
                Please verify the product details before marking client entry.
              </p>
              <div className="mb-3">
                <label htmlFor="verifiedProductName" className="form-label">
                  Verified Product Name
                </label>
                <InputField
                  type="text"
                  className={`form-control ${
                    modalErrors.verifiedProductName ? "is-invalid" : ""
                  }`}
                  id="verifiedProductName"
                  value={verifiedProductName}
                  onChange={(e) => {
                    setVerifiedProductName(e.target.value);
                    if (modalErrors.verifiedProductName) setModalErrors({});
                  }}
                  required
                />
                {modalErrors.verifiedProductName && (
                  <div className="invalid-feedback mb-3">
                    {modalErrors.verifiedProductName}
                  </div>
                )}
                <p className="bgcard">
                  <span className="fw-bold"> {currentDateTime} </span>  
                  will be saved automatically once you click OK.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <Button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </Button>
              <Button
                type="button"
                className="btn btn-primary"
                onClick={handleOkayEnter}
              >
                Okay
              </Button>
            </div>
          </div>
        </div>
      </div>
 
      <div
        className="modal fade"
        id="exitModal"
        tabIndex="-1"
        aria-labelledby="exitModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exitModalLabel">
                Mark Exit
              </h5>
              <Button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></Button>
            </div>
            <div className="modal-body">
              <div className="bgcard">
                Please confirm that the client is exiting the warehouse.
                <span className="fw-bold"> {currentDateTime} </span>
                will be saved automatically once you click OK.
              </div>
            </div>
            <div className="modal-footer">
              <Button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </Button>
 
              <Button
                type="button"
                className="btn btn-primary"
                onClick={handleOkayExit}
              >
                Okay
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
 
export default WarehouseBookings;
 